/**
 * `resolveGrants` — PURE core FUNCTION of permissions engine (🎯 F0b).
 *
 * No DOM, no framework, no IO: (account, tree, grants) -> set of
 * effective permissions. Semantic reference that server compiles to
 * `effective_grant` (and thence to PowerSync buckets).
 *
 * ## Algoritmo (contrato — docs/SCOPED_PERMISSIONS_GUIDE.md)
 *
 * To decide level `L` of capability `C` at node `N`, traverse scope
 * chain of `N` toward root (N, parent, ..., org). At each step
 * `S`, from MOST specific to least:
 *
 * 1. Grants of `C` with scope `S` matching subject apply (account or
 *    assigned role). If `S !== N`, grant requires `inherit` (inheritance
 *    to descendants is opt-in per grant).
 * 2. A grant is DECISIVE for `L` if:
 *    - `allow` of action `A`: decides (allows) levels `L <= A`;
 *    - `deny` of action `A`: decides (denies) levels `L >= A`
 *      (denying read denies all; denying manage only denies manage).
 * 3. With decision at `S`: subject `user` grants PRECEDE role grants
 *    (if direct user decision exists, role grants ignored). Within
 *    chosen class, **deny wins**.
 * 4. Without decision at `S` -> walk up one step. Without decision across entire
 *    chain -> DENIED (missing capability = no access).
 */

import {
  ACTION_LEVEL,
  type EffectivePermission,
  type GrantAction,
  type GrantSubject,
  type PermissionGrantInput,
  type ScopeNode,
} from "./types";

/** Tree index + precalculated scope chains. */
export interface ScopeTreeIndex {
  byId: ReadonlyMap<string, ScopeNode>;
  /** nodeId -> [nodeId, parent, ..., root] */
  chains: ReadonlyMap<string, readonly string[]>;
}

/** Indexes tree and precomputes scope chain of each node (anti-cycle). */
export function indexScopeTree(nodes: readonly ScopeNode[]): ScopeTreeIndex {
  const byId = new Map<string, ScopeNode>();
  for (const node of nodes) {
    byId.set(node.id, node);
  }
  const chains = new Map<string, readonly string[]>();
  for (const node of nodes) {
    const chain: string[] = [];
    const seen = new Set<string>();
    let cursor: ScopeNode | undefined = node;
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      chain.push(cursor.id);
      cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
    }
    chains.set(node.id, chain);
  }
  return { byId, chains };
}

function subjectMatches(grant: PermissionGrantInput, subject: GrantSubject): boolean {
  return grant.subjectKind === "user"
    ? grant.subjectId === subject.userId
    : subject.roleIds.includes(grant.subjectId);
}

/** Does this grant decide level `level`? (allow downward, deny upward). */
function isDecisive(grant: PermissionGrantInput, level: GrantAction): boolean {
  const grantLevel = ACTION_LEVEL[grant.action];
  const asked = ACTION_LEVEL[level];
  return grant.effect === "allow" ? asked <= grantLevel : asked >= grantLevel;
}

/**
 * Decision of ONE action level on ONE node. Exposed for precision
 * algorithm testing; `resolveGrants` applies it to full tree.
 */
export function resolveLevel(
  subject: GrantSubject,
  tree: ScopeTreeIndex,
  grants: readonly PermissionGrantInput[],
  nodeId: string,
  capabilityId: string,
  level: GrantAction,
): boolean {
  const chain = tree.chains.get(nodeId) ?? [];
  for (const [distance, scopeId] of chain.entries()) {
    let userAllow = false;
    let userDeny = false;
    let roleAllow = false;
    let roleDeny = false;
    for (const grant of grants) {
      if (
        grant.capabilityId !== capabilityId ||
        grant.scopeId !== scopeId ||
        (distance > 0 && !grant.inherit) ||
        !subjectMatches(grant, subject) ||
        !isDecisive(grant, level)
      ) {
        continue;
      }
      if (grant.subjectKind === "user") {
        if (grant.effect === "allow") {
          userAllow = true;
        } else {
          userDeny = true;
        }
      } else if (grant.effect === "allow") {
        roleAllow = true;
      } else {
        roleDeny = true;
      }
    }
    // Precedence: user > role; within class, deny > allow.
    if (userAllow || userDeny) {
      return !userDeny;
    }
    if (roleAllow || roleDeny) {
      return !roleDeny;
    }
    // No decision in this scope -> continue walking up.
  }
  return false;
}

/**
 * CENTRAL PURE FUNCTION: effective permissions of subject over FULL tree.
 * One row per (capability, node) with some level granted — exactly what
 * server materializes in `effective_grant`. Deterministic stable order
 * (nodeId, capabilityId) para comparaciones deterministas.
 */
export function resolveGrants(
  subject: GrantSubject,
  nodes: readonly ScopeNode[],
  grants: readonly PermissionGrantInput[],
): EffectivePermission[] {
  const tree = indexScopeTree(nodes);
  const capabilities = [
    ...new Set(grants.filter((g) => subjectMatches(g, subject)).map((g) => g.capabilityId)),
  ].toSorted();

  const out: EffectivePermission[] = [];
  for (const node of nodes) {
    for (const capabilityId of capabilities) {
      const canRead = resolveLevel(subject, tree, grants, node.id, capabilityId, "read");
      const canWrite = resolveLevel(subject, tree, grants, node.id, capabilityId, "write");
      const canManage = resolveLevel(subject, tree, grants, node.id, capabilityId, "manage");
      if (canRead || canWrite || canManage) {
        out.push({
          capabilityId,
          nodeId: node.id,
          nodeKind: node.kind,
          churchId: node.churchId,
          canRead,
          canWrite,
          canManage,
        });
      }
    }
  }
  return out.toSorted((a, b) =>
    a.nodeId === b.nodeId
      ? a.capabilityId.localeCompare(b.capabilityId)
      : a.nodeId.localeCompare(b.nodeId),
  );
}

/**
 * UI helper: does this effective set grant requested level at node?
 * (To gate views/actions with SAME rows governing sync.)
 */
export function canAt(
  effective: readonly EffectivePermission[],
  nodeId: string,
  capabilityId: string,
  level: GrantAction,
): boolean {
  const row = effective.find((p) => p.nodeId === nodeId && p.capabilityId === capabilityId);
  if (!row) {
    return false;
  }
  const byLevel: Record<GrantAction, boolean> = {
    read: row.canRead,
    write: row.canWrite,
    manage: row.canManage,
  };
  return byLevel[level];
}
